/**
 * Tâche 1139 (Eric 2026-06-09) — Corbeille du dictionnaire.
 *
 * Le bouton « Corbeille » existait déjà sur la page Dictionnaire mais menait
 * à une route inexistante, et la suppression était définitive. Une définition
 * écrite en trente minutes ne doit pas disparaître sur un clic mal placé —
 * c'est exactement ce qui est arrivé pendant les tests de la tâche 1137.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowCounterClockwise,
  Trash,
  SpinnerGap,
  Warning,
} from "@phosphor-icons/react";
import { confirmDialog } from "../lib/uiDialogs";

const API = process.env.REACT_APP_BACKEND_URL;

export default function JargonTrashPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [retention, setRetention] = useState(30);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/jargon/trash`, {
        withCredentials: true,
      });
      setEntries(data?.entries || []);
      setRetention(data?.retention_days || 30);
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const restore = async (entry) => {
    setBusyId(entry.id);
    try {
      await axios.post(`${API}/api/jargon/${entry.id}/restore-deleted`, {}, {
        withCredentials: true,
      });
      toast.success(`« ${entry.term} » remis dans le dictionnaire.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restauration impossible");
    } finally {
      setBusyId("");
    }
  };

  const purge = async (entry) => {
    const ok = await confirmDialog(
      `Supprimer définitivement « ${entry.term} » ?\n\nCette action est irréversible : la fiche ne pourra plus être restaurée.`,
      { confirmLabel: "Supprimer définitivement" },
    );
    if (!ok) return;
    setBusyId(entry.id);
    try {
      await axios.delete(`${API}/api/jargon/trash/${entry.id}`, {
        withCredentials: true,
      });
      toast.success(`« ${entry.term} » supprimé définitivement.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="max-w-4xl" data-testid="jargon-trash-page">
      <button
        onClick={() => navigate("/dashboard/dictionnaire")}
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 mb-4 transition-colors hover:text-zinc-900"
        data-testid="trash-back-btn"
      >
        <ArrowLeft size={16} />
        Retour au dictionnaire
      </button>

      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900 mb-2">
        Corbeille du dictionnaire
      </h1>
      <p className="text-base text-zinc-600 mb-8">
        Les termes supprimés restent récupérables pendant{" "}
        <strong>{retention} jours</strong>, puis sont effacés définitivement.
      </p>

      {loading ? (
        <div className="py-16 text-center text-sm text-zinc-500">
          <SpinnerGap size={20} className="inline animate-spin mr-2" />
          Chargement…
        </div>
      ) : entries.length === 0 ? (
        <div
          className="py-16 text-center text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-lg"
          data-testid="trash-empty"
        >
          La corbeille est vide — aucun terme supprimé récemment.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="trash-list">
          {entries.map((e) => (
            <li
              key={e.id}
              className="border border-zinc-200 rounded-lg px-4 py-3 flex items-start gap-4 bg-white"
              data-testid={`trash-entry-${e.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-base font-semibold text-zinc-900">{e.term}</span>
                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                      e.days_left <= 3
                        ? "bg-rose-50 text-rose-700"
                        : "bg-zinc-100 text-zinc-500"
                    }`}
                    data-testid={`trash-days-${e.id}`}
                  >
                    {e.days_left <= 0
                      ? "purge imminente"
                      : `${e.days_left} jour(s) restant(s)`}
                  </span>
                  {e.days_left <= 3 && <Warning size={14} className="text-rose-600" />}
                </div>
                <p className="text-sm text-zinc-600 mt-1 line-clamp-2">{e.definition}</p>
                <p className="text-xs text-zinc-400 mt-1">
                  Supprimé par {e.deleted_by || "un utilisateur"}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => restore(e)}
                  disabled={busyId === e.id}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded bg-[#002FA7] text-white transition-colors hover:bg-[#00248a] disabled:opacity-50"
                  data-testid={`trash-restore-${e.id}`}
                >
                  <ArrowCounterClockwise size={14} />
                  Remettre
                </button>
                <button
                  onClick={() => purge(e)}
                  disabled={busyId === e.id}
                  title="Supprimer définitivement"
                  className="p-2 rounded text-zinc-400 transition-colors hover:text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                  data-testid={`trash-purge-${e.id}`}
                >
                  <Trash size={16} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
